import { Children, memo, type ReactNode } from "react";
import { PositionMenu } from "./menu/PositionMenu";
import { flexRender, type Table as TanStackTable, type RowModel } from "@tanstack/react-table";
import {
    type LockedPositionStore,
    type StakingPositionStore,
    type UnwindingPositionStore,
    useDialogPortal,
    useVoteValidation,
} from "@/hooks";
import { usePortfolioPositionsTableRowMenu } from "./menu/usePortfolioPositionsTableRowMenu";
import type { PositionStatus } from "./row/position-table-row.utils";
import { LOCK_MAX_DURATION } from "@/config";

type PositionData<T extends "locked" | "staked"> = T extends "locked" ? LockedPositionStore : StakingPositionStore;
type MenuPosition<T extends "locked" | "staked"> = T extends "locked"
    ? LockedPositionStore | UnwindingPositionStore
    : StakingPositionStore;

interface PortfolioPositionsTableProps<T extends "locked" | "staked"> {
    positionType: T;
    isLoading: boolean;
    table: TanStackTable<PositionData<T>>;
    rowModel: RowModel<PositionData<T>>;
    renderRow: (
        row: { id: string; index: number; original: PositionData<T> },
        menuProps: {
            isMenuOpen: boolean;
            menuIndex: number | null;
            onMenuToggle: (
                event: React.MouseEvent<HTMLElement>,
                position: MenuPosition<T>,
                index: number,
                status?: PositionStatus,
            ) => void;
        },
    ) => ReactNode;
    renderSkeletonRow: (index: number) => ReactNode;
    renderExtraRows?: (menuProps: {
        isMenuOpen: boolean;
        menuIndex: number | null;
        onMenuToggle: (
            event: React.MouseEvent<HTMLElement>,
            position: MenuPosition<T>,
            index: number,
            status?: PositionStatus,
        ) => void;
    }) => ReactNode;
}

export const PortfolioPositionsTable = memo(
    <T extends "locked" | "staked">({
        positionType,
        isLoading,
        table,
        rowModel,
        renderRow,
        renderSkeletonRow,
        renderExtraRows,
    }: PortfolioPositionsTableProps<T>) => {
        const { openDialog } = useDialogPortal();
        const { alreadyVoted, unwindingEpochs } = useVoteValidation();
        const {
            canStartUnlock,
            canExitEarly,
            handleExitEarly,
            handleClaim,
            handleMenuClose,
            handleMenuToggle,
            handleCancelUnlock,
            canCancelUnlock,
            handleWithdraw,
            handleChangeDuration,
            isMenuOpen,
            menuAnchorEl,
            menuIndex,
            menuPosition,
            menuStatus,
        } = usePortfolioPositionsTableRowMenu({
            positionType,
            alreadyVoted,
            unwindingEpochs,
            openDialog,
        });

        const extraRows = renderExtraRows
            ? renderExtraRows({
                  isMenuOpen,
                  menuIndex,
                  onMenuToggle: handleMenuToggle,
              })
            : null;
        const extraRowCount = Children.count(extraRows);
        const hasPositions = (rowModel?.rows?.length ?? 0) + extraRowCount > 0;

        return (
            <div
                id="positions"
                className="table-container overflow-x-auto rounded-lg border border-portfolio-positions-table shadow-portfolio-positions-table"
            >
                <table className="w-full min-w-full border-collapse ">
                    <thead>
                        {table?.getHeaderGroups().map((headerGroup) => (
                            <tr
                                key={headerGroup.id}
                                className="bg-portfolio-positions-table-header text-left text-portfolio-positions-table-header text-[12px] leading-[12px] font-normal"
                            >
                                {headerGroup.headers.map((header) => {
                                    const widthClass = header.column.columnDef.meta?.widthClass ?? "";

                                    return (
                                        <th key={header.id} className={`p-4 ${widthClass}`}>
                                            {header.isPlaceholder
                                                ? null
                                                : flexRender(header.column.columnDef.header, header.getContext())}
                                        </th>
                                    );
                                })}
                            </tr>
                        ))}
                    </thead>
                    <tbody className="bg-portfolio-positions-table-body">
                        {isLoading && Array.from({ length: 2 }).map((_, index) => renderSkeletonRow(index))}
                        {!isLoading &&
                            hasPositions &&
                            rowModel.rows.map((row) =>
                                renderRow(row, {
                                    isMenuOpen,
                                    menuIndex,
                                    onMenuToggle: handleMenuToggle,
                                }),
                            )}
                        {!isLoading && extraRowCount > 0 && extraRows}
                        {!isLoading && !hasPositions && (
                            <tr className="border-t border-portfolio-positions-table">
                                <td className="p-6 text-center text-[14px] text-dark" colSpan={5}>
                                    No positions found.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
                {menuPosition && (
                    <PositionMenu
                        positionType={positionType}
                        positionStatus={menuStatus}
                        isMenuOpen={isMenuOpen}
                        menuAnchorEl={menuAnchorEl}
                        menuPosition={menuPosition}
                        onMenuClose={handleMenuClose}
                        onWithdraw={handleWithdraw}
                        onClaim={handleClaim}
                        onCancelUnlock={handleCancelUnlock}
                        canCancelUnlock={canCancelUnlock}
                        canStartUnlock={canStartUnlock}
                        canExitEarly={canExitEarly}
                        onExitEarly={handleExitEarly}
                        onChangeDuration={handleChangeDuration}
                        canChangeDuration={(position) => {
                            if (positionType !== "locked") {
                                return {
                                    canChange: false,
                                    reason: "Only locked positions can change duration.",
                                };
                            }

                            if (!("lockDuration" in position)) {
                                return {
                                    canChange: false,
                                    reason: "Position lock duration is unavailable.",
                                };
                            }

                            const currentDuration = Number.parseInt(position.lockDuration.split("w")[0], 10);

                            if (Number.isNaN(currentDuration)) {
                                return {
                                    canChange: false,
                                    reason: "Unable to determine current lock duration.",
                                };
                            }

                            if (currentDuration >= LOCK_MAX_DURATION) {
                                return {
                                    canChange: false,
                                    reason: `Lock duration is already at the maximum of ${LOCK_MAX_DURATION} weeks.`,
                                };
                            }

                            const canChange = canStartUnlock(position);

                            return {
                                canChange,
                                reason: canChange
                                    ? undefined
                                    : "Position is not eligible to change duration right now.",
                            };
                        }}
                    />
                )}
            </div>
        );
    },
) as <T extends "locked" | "staked">(props: PortfolioPositionsTableProps<T>) => JSX.Element;

(PortfolioPositionsTable as unknown as { displayName?: string }).displayName = "PortfolioPositionsTable";
